import { createContext, useContext, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { getCurrentUser, login as apiLogin, logout as apiLogout, register as apiRegister, type ApiUser } from './services/api'
import { clearGoogleSession } from './services/googleTasks'

interface AuthContextValue {
  user: ApiUser
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function AuthScreen({ onAuthenticated }: { onAuthenticated: (user: ApiUser) => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const user = mode === 'login'
        ? await apiLogin(email.trim(), password)
        : await apiRegister(email.trim(), password)
      onAuthenticated(user)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not connect to ThriveCove.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-card">
        <div className="auth-brand"><span>TC</span><strong>ThriveCove</strong></div>
        <p className="eyebrow">Your private focus cove</p>
        <h1>{mode === 'login' ? 'Welcome back.' : 'Create your cove.'}</h1>
        <p className="auth-intro">Your focus sessions, task history, fish, and rewards are saved to your own account.</p>
        <form onSubmit={submit} className="auth-form">
          <label>Email
            <input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" />
          </label>
          <label>Password
            <input type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} placeholder="At least 8 characters" />
          </label>
          {error && <p className="auth-error">{error}</p>}
          <button className="primary large wide" type="submit" disabled={busy}>{busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}</button>
        </form>
        <button className="text-button auth-switch" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError('') }}>
          {mode === 'login' ? 'New here? Create an account' : 'Already have an account? Sign in'}
        </button>
        <p className="auth-footnote">Local Docker setup · password is stored as a one-way hash · session uses an HTTP-only cookie.</p>
      </section>
    </main>
  )
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [connectionError, setConnectionError] = useState('')

  useEffect(() => {
    let cancelled = false
    getCurrentUser()
      .then((current) => {
        if (!cancelled) setUser(current)
      })
      .catch(() => {
        if (!cancelled) setConnectionError('Cannot reach the ThriveCove database service. Make sure Docker is running.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  async function signOut() {
    let logoutError = ''
    try {
      await apiLogout()
    } catch (error) {
      logoutError = error instanceof Error
        ? error.message
        : 'Could not reach the ThriveCove database service to fully sign out.'
    } finally {
      // End the browser session regardless of the server result: drop this
      // tab's Google credentials and the in-memory user so no private state
      // stays visible. Persisted per-user progress is intentionally kept.
      clearGoogleSession()
      setConnectionError(logoutError)
      setUser(null)
    }
  }

  const value = useMemo<AuthContextValue | null>(() => user ? { user, logout: signOut } : null, [user])

  if (loading) return <div className="app-loading"><div className="loading-fish">◁</div><span>Connecting to your cove…</span></div>
  if (!user) return (
    <>
      {connectionError && <div className="api-warning">{connectionError}</div>}
      <AuthScreen onAuthenticated={(authenticated) => { setConnectionError(''); setUser(authenticated) }} />
    </>
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
